import { createReducer, on } from '@ngrx/store';

import {
  InspectionQueryType,
  InspectionsListConstants,
} from '@main-application/inspections/components/inspections-list/constants';
import {
  InspectionSummaryAppliedFilters,
  InspectionSummaryStatsResponse,
} from '@main-application/inspections/models/legacy-inspection-summary.model';
import getInspectionsSummaryFiltersKey from '@main-application/inspections/utils/inspection-mappers';
import {
  BulkInspectionFilterForm,
  IInspectionFilterForm,
  InReviewInspectionFilterForm,
  RepeatingInspectionFilterForm,
} from '@template/interfaces/filter.interface';
import { RestTemplateModel } from '@template/models/rest-template-model.interface';
import {
  InspectionsSummaryStatsDetails,
  SummaryStatsGroup,
} from '@ui-components/components/summary-expandable-table/summary-expandable-table.model';

import {
  clearAllInspections,
  clearDeletedDynamicInspectionId,
  clearDeletedRepeatingInspectionId,
  clearDynamicInspections,
  clearInspectionsData,
  clearNewlyAddedInspectionId,
  clearRepeatingInspections,
  deleteDynamicInspectionFailed,
  deleteDynamicInspectionSuccess,
  deleteRepeatingInspectionFailed,
  deleteRepeatingInspectionSuccess,
  loadAllInspections,
  loadAllInspectionsFailed,
  loadAllInspectionsSuccess,
  loadBulkInspections,
  loadBulkInspectionsFailed,
  loadBulkInspectionsSuccess,
  loadDynamicInspections,
  loadDynamicInspectionsFailed,
  loadDynamicInspectionsSuccess,
  loadInReviewInspections,
  loadInReviewInspectionsSuccess,
  loadInspectionSummaryReport,
  loadInspectionSummaryReportFailed,
  loadInspectionSummaryReportSucceeded,
  loadInspectionsSummaryDetails,
  loadInspectionsSummaryDetailsFailed,
  loadInspectionsSummaryDetailsSuccess,
  loadInspectionsSummaryStats,
  loadInspectionsSummaryStatsFailed,
  loadInspectionsSummaryStatsSuccess,
  loadInspectionsSummaryStatsV2,
  loadInspectionsSummaryStatsV2Failed,
  loadInspectionsSummaryStatsV2Success,
  loadRepeatingInspections,
  loadRepeatingInspectionsFailed,
  loadRepeatingInspectionsSuccess,
  loadResidentInspections,
  loadResidentInspectionsFailed,
  loadResidentInspectionsSuccess,
  loadTemplatesForUnitFailed,
  loadTemplatesForUnitSuccess,
  setBulkInspectionFilter,
  setInReviewInspectionsFilter,
  setInspectionsFilter,
  setRepeatingInspectionsFilter,
  setSummaryReportAppliedFilter,
  updateInspectionsFilter,
  updateRepeatingInspectionsFilter,
} from './inspections.actions';
import { RestDynamicInspectionModel, RestInspectionsModel } from '../models/rest-inspections-model.interface';

import TimeFrame = InspectionsListConstants.TimeFrame;

export const inspectionsFeatureKey = 'inspections';

export interface IInspectionsStateTree {
  readonly [inspectionsFeatureKey]: InspectionState;
}
export interface InspectionState {
  inspectionFilter: Partial<IInspectionFilterForm>;
  bulkInspectionFilter: Partial<BulkInspectionFilterForm>;
  inReviewInspectionFilter: Partial<InReviewInspectionFilterForm>;
  repeatingInspectionFilter: Partial<RepeatingInspectionFilterForm>;
  isLoaded: boolean;
  listItemsLoading?: boolean;
  inReviewListItemsLoading?: boolean;
  residentListItemsLoading?: boolean;
  dynamicListItemsLoading?: boolean;
  repeatingListItemsLoading?: boolean;
  bulkListItemsLoading?: boolean;
  listItems?: RestInspectionsModel[];
  inReviewListItems?: RestInspectionsModel[];
  residentListItems?: RestInspectionsModel[];
  dynamicListItems?: RestDynamicInspectionModel[];
  repeatingListItems?: RestInspectionsModel[];
  bulkListItems?: RestInspectionsModel[];
  templatesForUnit?: RestTemplateModel[];
  entityUpdateOngoing?: boolean;
  deleteEntityOngoing?: boolean;
  summaryStats: { [filterKey: string]: InspectionSummaryStatsResponse };
  summaryStatsAppliedFilters: InspectionSummaryAppliedFilters;
  summaryReportDataLoading?: boolean;
  removedDynamicInspectionId: number;
  removedRepeatingInspectionId: number;
  inspectionSummaryStats?: SummaryStatsGroup[];
  inspectionSummaryDetails?: InspectionsSummaryStatsDetails;
  inspectionSummaryLoading?: boolean;
  inspectionSummaryError?: any;
}

export const initialState: InspectionState = {
  inspectionFilter: {
    timeframe: TimeFrame.ThisMonth,
    status: null,
    unitOrAssignee: null,
    templateIds: null,
    assigneeId: null,
  },
  bulkInspectionFilter: {
    timeframe: TimeFrame.ThisMonth,
    unitOrAssignee: null,
    assigneeId: null,
    templateIds: null,
    status: null,
    queryType: InspectionQueryType.Active,
    startDate: null,
    endDate: null,
  },
  inReviewInspectionFilter: {
    unitOrAssignee: null,
    templateIds: null,
    assigneeId: null,
    parentInspectionId: null,
    dynamicInspectionId: null,
  },
  repeatingInspectionFilter: {
    unitOrAssignee: null,
    templateIds: null,
    assigneeId: null,
    recurrencePeriod: null,
  },
  isLoaded: false,
  listItems: [],
  inReviewListItems: [],
  listItemsLoading: false,
  residentListItemsLoading: false,
  inReviewListItemsLoading: false,
  dynamicListItemsLoading: false,
  entityUpdateOngoing: false,
  deleteEntityOngoing: false,
  summaryStats: {},
  summaryStatsAppliedFilters: null,
  summaryReportDataLoading: true,
  removedDynamicInspectionId: null,
  removedRepeatingInspectionId: null,
  inspectionSummaryStats: null,
  inspectionSummaryDetails: null,
  inspectionSummaryLoading: false,
  inspectionSummaryError: null,
};

export const reducer = createReducer(
  initialState,
  /* Templates List */
  on(loadAllInspections, (state, action) => ({ ...state, listItemsLoading: true })),
  on(loadAllInspectionsSuccess, (state, action) => ({
    ...state,
    listItemsLoading: false,
    listItems: action.listItems,
    deleteEntityOngoing: false,
  })),
  on(loadAllInspectionsFailed, (state, action) => ({
    ...state,
    listItemsLoading: false,
    listItems: [],
    deleteEntityOngoing: false,
  })),
  on(clearAllInspections, state => ({
    ...state,
    listItems: [],
  })),
  on(loadResidentInspections, (state, action) => ({ ...state, residentListItemsLoading: true })),
  on(loadResidentInspectionsSuccess, (state, action) => ({
    ...state,
    residentListItemsLoading: false,
    residentListItems: action.listItems,
  })),
  on(loadResidentInspectionsFailed, state => ({
    ...state,
    residentListItemsLoading: false,
  })),
  on(loadDynamicInspections, (state, action) => ({ ...state, dynamicListItemsLoading: true })),
  on(loadDynamicInspectionsSuccess, (state, action) => ({
    ...state,
    dynamicListItemsLoading: false,
    dynamicListItems: action.listItems,
  })),
  on(loadDynamicInspectionsFailed, state => ({
    ...state,
    dynamicListItemsLoading: false,
  })),
  on(clearDynamicInspections, state => ({
    ...state,
    dynamicListItems: null,
  })),
  on(deleteDynamicInspectionSuccess, (state, action) => ({
    ...state,
    removedDynamicInspectionId: action.inspectionId,
    dynamicListItems: state.dynamicListItems.filter(inspection => inspection.id !== action.inspectionId),
  })),
  on(deleteDynamicInspectionFailed, state => {
    return {
      ...state,
      removedDynamicInspectionId: null,
    };
  }),
  on(clearDeletedDynamicInspectionId, state => ({
    ...state,
    removedDynamicInspectionId: null,
  })),
  on(loadRepeatingInspections, (state, action) => ({ ...state, repeatingListItemsLoading: true })),
  on(loadRepeatingInspectionsSuccess, (state, action) => ({
    ...state,
    repeatingListItemsLoading: false,
    repeatingListItems: action.listItems,
  })),
  on(loadRepeatingInspectionsFailed, state => ({
    ...state,
    repeatingListItemsLoading: false,
  })),
  on(loadBulkInspections, (state, action) => ({ ...state, bulkListItemsLoading: true })),
  on(loadBulkInspectionsSuccess, (state, action) => ({
    ...state,
    bulkListItemsLoading: false,
    bulkListItems: action.listItems,
  })),
  on(loadBulkInspectionsFailed, state => ({
    ...state,
    bulkListItemsLoading: false,
  })),
  on(deleteRepeatingInspectionSuccess, (state, action) => ({
    ...state,
    removedRepeatingInspectionId: action.inspectionId,
    repeatingListItems: state.repeatingListItems.filter(inspection => inspection.id !== action.inspectionId),
  })),
  on(deleteRepeatingInspectionFailed, state => {
    return {
      ...state,
      removedRepeatingInspectionId: null,
    };
  }),
  on(clearDeletedRepeatingInspectionId, state => ({
    ...state,
    removedRepeatingInspectionId: null,
  })),
  on(clearRepeatingInspections, state => ({
    ...state,
    repeatingListItems: null,
  })),
  on(clearInspectionsData, (state, action) => ({
    ...state,
    listItemsLoading: false,
    entityUpdateOngoing: false,
    deleteEntityOngoing: false,
  })),
  on(loadTemplatesForUnitSuccess, (state, action) => ({
    ...state,
    templatesForUnit: action.list,
  })),
  on(loadTemplatesForUnitFailed, (state, action) => ({
    ...state,
    templatesForUnit: [],
  })),
  on(clearNewlyAddedInspectionId, (state, action) => ({
    ...state,
    newlyAddedItemId: null,
  })),
  on(setInspectionsFilter, (state, action) => ({
    ...state,
    inspectionFilter: action,
  })),
  on(updateInspectionsFilter, (state, action) => ({
    ...state,
    inspectionFilter: {
      ...state.inspectionFilter,
      ...action,
    },
  })),
  on(setBulkInspectionFilter, (state, action) => ({
    ...state,
    bulkInspectionFilter: action,
  })),
  on(setInReviewInspectionsFilter, (state, action) => ({
    ...state,
    inReviewInspectionFilter: action,
  })),
  on(loadInspectionSummaryReport, (state, action) => ({
    ...state,
    summaryReportDataLoading: true,
    listItemsLoading: true,
  })),
  on(loadInspectionSummaryReportSucceeded, (state, action) => ({
    ...state,
    summaryReportDataLoading: false,
    listItemsLoading: false,
    summaryStats: {
      ...state.summaryStats,
      [getInspectionsSummaryFiltersKey(action.filter)]: action.payload,
    },
  })),
  on(setSummaryReportAppliedFilter, (state, action) => ({
    ...state,
    summaryStatsAppliedFilters: action.appliedFilters,
  })),
  on(setRepeatingInspectionsFilter, (state, action) => ({
    ...state,
    repeatingInspectionFilter: action,
  })),
  on(updateRepeatingInspectionsFilter, (state, action) => ({
    ...state,
    repeatingInspectionFilter: {
      ...state.repeatingInspectionFilter,
      ...action,
    },
  })),
  on(loadInspectionSummaryReportFailed, state => ({
    ...state,
    repeatingListItemsLoading: false,
    error: true,
  })),
  on(loadInReviewInspections, (state, action) => ({ ...state, listItemsLoading: true })),
  on(loadInReviewInspectionsSuccess, (state, action) => ({
    ...state,
    inReviewListItemsLoading: false,
    inReviewListItems: action.inReviewListItems,
  })),
  on(loadAllInspectionsFailed, (state, action) => ({
    ...state,
    inReviewListItemsLoading: false,
    inReviewListItems: [],
  })),
  on(clearAllInspections, state => ({
    ...state,
    inReviewListItems: [],
  })),
  on(loadInspectionsSummaryStats, state => ({
    ...state,
    inspectionSummaryLoading: true,
    inspectionSummaryError: null,
  })),

  on(loadInspectionsSummaryStatsSuccess, (state, action) => ({
    ...state,
    inspectionSummaryStats: action.stats,
    inspectionSummaryLoading: false,
  })),

  on(loadInspectionsSummaryStatsFailed, (state, action) => ({
    ...state,
    inspectionSummaryError: action.error,
    inspectionSummaryLoading: false,
  })),

  on(loadInspectionsSummaryDetails, state => ({
    ...state,
    inspectionSummaryLoading: true,
    inspectionSummaryError: null,
  })),

  on(loadInspectionsSummaryDetailsSuccess, (state, action) => ({
    ...state,
    inspectionSummaryDetails: action.details,
    inspectionSummaryLoading: false,
  })),

  on(loadInspectionsSummaryDetailsFailed, (state, action) => ({
    ...state,
    inspectionSummaryError: action.error,
    inspectionSummaryLoading: false,
  })),

  on(loadInspectionsSummaryStatsV2, state => ({
    ...state,
    inspectionSummaryLoading: true,
    inspectionSummaryError: null,
  })),

  on(loadInspectionsSummaryStatsV2Success, (state, { stats }) => ({
    ...state,
    inspectionSummaryStats: stats,
    inspectionSummaryLoading: false,
  })),

  on(loadInspectionsSummaryStatsV2Failed, (state, { error }) => ({
    ...state,
    inspectionSummaryError: error,
    inspectionSummaryLoading: false,
  }))
);
